import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import artistApi from '@/lib/artistApi';
import { useArtistAuth } from '@/context/ArtistAuthContext';
import { useCollaboratorAuth } from '@/context/CollaboratorAuthContext';

const emptyFilters = { location: '', skill: '', project_type: '', budget: '' };

const CollabMatch = () => {
  const { artist, loading: artistLoading } = useArtistAuth();
  const { collaborator, loading: collaboratorLoading } = useCollaboratorAuth();
  const [filters, setFilters] = useState(emptyFilters);
  const [candidates, setCandidates] = useState([]);
  const [matches, setMatches] = useState([]);
  const [candidateRole, setCandidateRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [referralNote, setReferralNote] = useState('');

  const activeRole = useMemo(() => {
    if (artist) return 'artist';
    if (collaborator) return 'collaborator';
    return '';
  }, [artist, collaborator]);
  const activeUser = artist || collaborator;
  const authLoading = artistLoading || collaboratorLoading;

  const loadMatches = async () => {
    if (!activeRole) return;
    setLoading(true);
    setMessage('');
    try {
      const endpoint = activeRole === 'artist' ? '/artist-auth/matches' : '/collaborator-auth/matches';
      const response = await artistApi.get(endpoint, { params: filters });
      setCandidates(response.data.candidates || []);
      setMatches(response.data.matches || []);
      setCandidateRole(response.data.candidate_role || '');
      if (!(response.data.candidates || []).length) {
        setMessage('No fresh suggestions yet. Try city, skill, or project filters.');
      }
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not load collaboration matches.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRole]);

  const saveAction = async (candidate, action) => {
    try {
      const endpoint = activeRole === 'artist' ? '/artist-auth/matches/action' : '/collaborator-auth/matches/action';
      const response = await artistApi.post(endpoint, { target_id: candidate.id, action });
      setMessage(
        response.data.matched
          ? 'It is a mutual match. A PMB conversation is ready.'
          : action === 'like'
            ? `Liked ${candidate.display_name}. You will match when they like back.`
            : `Passed ${candidate.display_name}.`
      );
      await loadMatches();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not save this match action.');
    }
  };

  const referCandidate = async (candidate) => {
    try {
      await artistApi.post('/artist-referrals', {
        target_id: candidate.id,
        target_role: candidate.role,
        context: `Collab Finder suggestion for ${activeUser?.name || activeUser?.organization_name || 'KalaManch user'}`,
        note: referralNote || `Recommended ${candidate.display_name} for a relevant creative opportunity.`,
        source: 'collab_match',
      });
      setReferralNote('');
      setMessage(`Referral recorded for ${candidate.display_name}.`);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not record referral.');
    }
  };

  return (
    <main className="min-h-screen bg-[#f7f9fc] pt-28 pb-16">
      <SEO
        title="KalaManch Collab Finder"
        description="Find AI-assisted collaboration suggestions between artists and collaborators on KalaManch."
        keywords="artist collaboration finder, KalaManch AI collaboration, Indian creators collaboration"
      />
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-purple-100 bg-white p-5 shadow-xl sm:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#FF6F00]">AI Collaboration Matchmaking</p>
          <div className="mt-3 grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <h1 className="text-4xl font-black text-slate-950">Find your next creative match</h1>
              <p className="mt-3 max-w-3xl text-gray-600">
                KalaManch suggests collaboration-ready {candidateRole || 'profiles'} using city, skill, project needs, verification, and profile text.
              </p>
            </div>
            <Link to="/event-planner" className="rounded-full border border-orange-200 px-5 py-3 text-center text-sm font-bold text-[#FF6F00] hover:bg-orange-50">
              Plan event team
            </Link>
          </div>

          {!authLoading && !activeRole && (
            <div className="mt-6 rounded-2xl border border-orange-100 bg-orange-50 p-4 text-sm font-semibold text-orange-900">
              Login as an artist or collaborator to use Collab Finder.
            </div>
          )}

          {activeRole && (
            <>
              <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {Object.entries(filters).map(([key, value]) => (
                  <input
                    key={key}
                    value={value}
                    onChange={(event) => setFilters((current) => ({ ...current, [key]: event.target.value }))}
                    placeholder={key.replace('_', ' ')}
                    className="rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100"
                  />
                ))}
              </div>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <button type="button" onClick={loadMatches} disabled={loading} className="btn-primary disabled:bg-gray-300">
                  {loading ? 'Finding...' : 'Refresh Matches'}
                </button>
                <button type="button" onClick={() => setFilters(emptyFilters)} className="btn-outline">
                  Clear Filters
                </button>
              </div>

              {message && <div className="mt-4 rounded-2xl bg-purple-50 px-4 py-3 text-sm font-semibold text-purple-900">{message}</div>}

              {matches.length > 0 && (
                <div className="mt-7 rounded-3xl border border-emerald-100 bg-emerald-50 p-4">
                  <h2 className="text-xl font-black text-slate-950">Mutual matches</h2>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {matches.map((match) => (
                      <div key={`${match.conversation_id}-${match.target?.id}`} className="rounded-2xl bg-white p-4">
                        <p className="font-bold text-gray-950">{match.target?.display_name || 'Matched profile'}</p>
                        <p className="mt-1 text-sm text-gray-600">{match.target?.headline}</p>
                        <Link to={activeRole === 'artist' ? '/artist/inbox' : '/collaborator/inbox'} className="mt-3 inline-flex text-sm font-bold text-[#FF6F00]">
                          Open PMB
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-7 grid gap-4 lg:grid-cols-2">
                {candidates.map((candidate) => (
                  <article key={candidate.id} className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="flex gap-4">
                      {candidate.image_url ? (
                        <img src={candidate.image_url} alt="" className="h-20 w-20 rounded-2xl object-cover" />
                      ) : (
                        <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-orange-50 text-2xl font-black text-[#FF6F00]">
                          {(candidate.display_name || 'K').charAt(0)}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="text-xl font-black text-slate-950">{candidate.display_name}</h2>
                          <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-bold text-[#4A148C]">{candidate.match_score}% match</span>
                        </div>
                        <p className="mt-1 text-sm font-semibold text-gray-700">{candidate.headline}</p>
                        <p className="mt-1 text-sm text-gray-500">{candidate.location || 'India'}</p>
                      </div>
                    </div>
                    <p className="mt-4 line-clamp-2 text-sm leading-6 text-gray-600">{candidate.bio || candidate.project_type_label || 'Open to relevant KalaManch collaborations.'}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {(candidate.match_reasons || []).map((reason) => (
                        <span key={reason} className="rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-700">{reason}</span>
                      ))}
                    </div>
                    <textarea
                      value={referralNote}
                      onChange={(event) => setReferralNote(event.target.value)}
                      placeholder="Optional referral note"
                      className="mt-4 min-h-[70px] w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#00BFA5]"
                    />
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                      <button type="button" onClick={() => saveAction(candidate, 'like')} className="btn-primary">Like</button>
                      <button type="button" onClick={() => saveAction(candidate, 'pass')} className="btn-outline">Pass</button>
                      <button type="button" onClick={() => referCandidate(candidate)} className="rounded-full border border-emerald-200 px-5 py-3 text-sm font-bold text-emerald-700 hover:bg-emerald-50">
                        Refer
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
};

export default CollabMatch;
